import { createContext, useContext, useId, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** Read the enclosing Field's ids so a control can wire label/help/error itself. */
export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  /** Mono uppercase label. */
  label: string;
  /** Explicit control id; generated when omitted. */
  id?: string;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Right-aligned mono hint next to the label (e.g. `OPTIONAL`, character count). */
  hint?: ReactNode;
  /** Inline layout: label left (w-40), control right. */
  inline?: boolean;
  children: ReactNode;
  className?: string;
}

/** Mono label + control + help/error. Controls inside pick the id through context. */
export function Field({ label, id: explicitId, help, error, required = false, hint, inline = false, children, className }: FieldProps) {
  const generated = useId();
  const id = explicitId ?? `field-${generated}`;
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(' ') || undefined;
  const ctx: FieldContextValue = { id, describedBy, invalid: Boolean(error), required };
  return (
    <FieldContext.Provider value={ctx}>
      <div className={cn(inline ? 'grid grid-cols-[160px_1fr] items-start gap-x-4' : 'flex flex-col', className)}>
        <div className={cn('flex items-baseline justify-between gap-3', inline ? 'pt-1.5' : 'mb-1.5')}>
          <label htmlFor={id} className="label-mono cursor-default">
            {label}
            {required ? <span aria-hidden className="ml-1 text-brand-text">*</span> : null}
          </label>
          {hint ? <span className="font-mono text-2xs text-mute">{hint}</span> : null}
        </div>
        <div className="min-w-0">
          {children}
          {error ? (
            <p id={errorId} role="alert" className="mt-1.5 text-xs text-brand-text">
              {error}
            </p>
          ) : null}
          {help ? (
            <p id={helpId} className="mt-1.5 text-xs text-mute">
              {help}
            </p>
          ) : null}
        </div>
      </div>
    </FieldContext.Provider>
  );
}
